import { useState, useEffect } from 'react';
import { getErrorMessage } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { lovable } from '@/integrations/lovable/index';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Eye, EyeOff, Link, CheckCircle2 } from 'lucide-react';

interface ProfileEditProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProfileEdit({ open, onOpenChange }: ProfileEditProps) {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [linkingProvider, setLinkingProvider] = useState<string | null>(null);
  const [profile, setProfile] = useState({
    full_name: '',
    username: '',
  });
  const [initialProfile, setInitialProfile] = useState({
    full_name: '',
    username: '',
  });
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Check which providers are already linked
  const linkedProviders = user?.identities?.map(i => i.provider) || [];
  const isEmailUser = linkedProviders.includes('email');
  const isGoogleLinked = linkedProviders.includes('google');
  const isAppleLinked = linkedProviders.includes('apple');

  const handleLinkProvider = async (provider: 'google' | 'apple') => {
    setLinkingProvider(provider);
    try {
      const { error } = await lovable.auth.signInWithOAuth(provider, {
        redirect_uri: window.location.origin,
      });
      if (error) {
        toast.error(`Gagal menghubungkan akun ${provider}: ${error.message}`);
      }
    } catch {
      toast.error(`Gagal menghubungkan akun ${provider}`);
    } finally {
      setLinkingProvider(null);
    }
  };

  useEffect(() => {
    if (open && user) {
      fetchProfile();
    }
  }, [open, user]);

  const fetchProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user?.id)
        .single();

      if (error) throw error;
      const loaded = {
        full_name: data.full_name || '',
        username: data.username || '',
      };
      setProfile(loaded);
      setInitialProfile(loaded);
    } catch (error) {
      toast.error('Failed to load profile: ' + getErrorMessage(error));
    }
  };

  const handleProfileUpdate = async () => {
    if (!user) return;
    setLoading(true);

    try {
      // Check if username is being changed and if it's already taken
      if (profile.username) {
        const { data: existingProfile, error: checkError } = await supabase
          .from('profiles')
          .select('username, id')
          .ilike('username', profile.username)
          .limit(1);
        
        // If error checking, log it but continue
        if (checkError) {
          console.error('Error checking username availability:', checkError);
        }
        
        // If username exists and belongs to a different user
        if (existingProfile && existingProfile.length > 0 && existingProfile[0].id !== user.id) {
          toast.error('Username tersebut sudah digunakan. Silakan pilih username lain.');
          setLoading(false);
          return;
        }
      }

      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: profile.full_name,
          username: profile.username || null,
        })
        .eq('id', user.id);

      if (error) {
        console.error('Update profile error:', error);
        // Handle duplicate username error
        if (error.code === '23505' && error.message.includes('profiles_username_key')) {
          toast.error('Username tersebut sudah digunakan. Silakan pilih username lain.');
          return;
        }
        throw error;
      }
      toast.success('Profile updated successfully');
      setInitialProfile({ ...profile });
    } catch (error) {
      console.error('Profile update exception:', error);
      toast.error('Failed to update profile: ' + getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!passwords.current || !passwords.new || !passwords.confirm) {
      toast.error('Silakan isi semua field password');
      return;
    }

    if (passwords.new === passwords.current) {
      toast.error('Password baru tidak boleh sama dengan password lama');
      return;
    }

    if (passwords.new !== passwords.confirm) {
      toast.error('Password baru tidak sama');
      return;
    }

    if (passwords.new.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }

    if (!/[0-9]/.test(passwords.new)) {
      toast.error('Password harus mengandung minimal 1 angka');
      return;
    }

    if (!/[a-zA-Z]/.test(passwords.new)) {
      toast.error('Password harus mengandung minimal 1 huruf');
      return;
    }

    setLoading(true);
    try {
      // First verify current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: passwords.current,
      });

      if (signInError) {
        toast.error('Password lama tidak benar');
        return;
      }

      // Update to new password
      const { error } = await supabase.auth.updateUser({
        password: passwords.new,
      });

      if (error) throw error;
      
      toast.success('Password changed successfully');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error('Failed to change password: ' + getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
          <DialogDescription>
            Update your profile information and password
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className={`grid w-full ${isAdmin ? 'grid-cols-3' : 'grid-cols-2'}`}>
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="accounts">Akun Terhubung</TabsTrigger>
            {isAdmin && <TabsTrigger value="password">Password</TabsTrigger>}
          </TabsList>

          <TabsContent value="profile" className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={user?.email || ''}
                disabled
                className="bg-muted"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name</Label>
              <Input
                id="full_name"
                value={profile.full_name}
                onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                placeholder="Masukkan nama"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={profile.username}
                onChange={(e) => setProfile({ ...profile, username: e.target.value })}
                placeholder="Masukkan username"
                disabled={!isAdmin}
                className={!isAdmin ? "bg-muted" : ""}
              />
            </div>

            <Button
              onClick={handleProfileUpdate}
              disabled={loading || (profile.full_name === initialProfile.full_name && profile.username === initialProfile.username)}
              className="w-full"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </TabsContent>

          <TabsContent value="accounts" className="space-y-4 pt-4">
            <p className="text-sm text-muted-foreground">
              Hubungkan akun Google atau Apple untuk login lebih mudah.
            </p>

            {/* Google */}
            <div className="flex items-center justify-between p-4 rounded-lg border border-border">
              <div className="flex items-center gap-3">
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <div>
                  <p className="text-sm font-medium">Google</p>
                  {isGoogleLinked && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Terhubung
                    </p>
                  )}
                </div>
              </div>
              {isGoogleLinked ? (
                <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">Linked</span>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleLinkProvider('google')}
                  disabled={linkingProvider === 'google'}
                  className="gap-2"
                >
                  <Link className="h-3 w-3" />
                  {linkingProvider === 'google' ? 'Menghubungkan...' : 'Hubungkan'}
                </Button>
              )}
            </div>

            {/* Apple */}
            <div className="flex items-center justify-between p-4 rounded-lg border border-border">
              <div className="flex items-center gap-3">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                <div>
                  <p className="text-sm font-medium">Apple</p>
                  {isAppleLinked && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Terhubung
                    </p>
                  )}
                </div>
              </div>
              {isAppleLinked ? (
                <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">Linked</span>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleLinkProvider('apple')}
                  disabled={linkingProvider === 'apple'}
                  className="gap-2"
                >
                  <Link className="h-3 w-3" />
                  {linkingProvider === 'apple' ? 'Menghubungkan...' : 'Hubungkan'}
                </Button>
              )}
            </div>
          </TabsContent>

          {isAdmin && (
            <TabsContent value="password" className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Current Password</Label>
              <div className="relative">
                <Input
                  id="current_password"
                  type={showCurrentPassword ? "text" : "password"}
                  value={passwords.current}
                  onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                  placeholder="Masukkan password lama"
                  className="pr-10"
                />
                {passwords.current && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onMouseDown={() => setShowCurrentPassword(true)}
                    onMouseUp={() => setShowCurrentPassword(false)}
                    onMouseLeave={() => setShowCurrentPassword(false)}
                    onTouchStart={() => setShowCurrentPassword(true)}
                    onTouchEnd={() => setShowCurrentPassword(false)}
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showNewPassword ? "text" : "password"}
                  value={passwords.new}
                  onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                  placeholder="Masukkan password baru"
                  className="pr-10"
                />
                {passwords.new && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onMouseDown={() => setShowNewPassword(true)}
                    onMouseUp={() => setShowNewPassword(false)}
                    onMouseLeave={() => setShowNewPassword(false)}
                    onTouchStart={() => setShowNewPassword(true)}
                    onTouchEnd={() => setShowNewPassword(false)}
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Minimal 6 karakter, harus kombinasi huruf dan angka
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={passwords.confirm}
                  onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                  placeholder="Masukkan konfirmasi password"
                  className="pr-10"
                />
                {passwords.confirm && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onMouseDown={() => setShowConfirmPassword(true)}
                    onMouseUp={() => setShowConfirmPassword(false)}
                    onMouseLeave={() => setShowConfirmPassword(false)}
                    onTouchStart={() => setShowConfirmPassword(true)}
                    onTouchEnd={() => setShowConfirmPassword(false)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                )}
              </div>
            </div>

            <Button
              onClick={handlePasswordChange}
              disabled={loading || !passwords.current || !passwords.new || !passwords.confirm}
              className="w-full"
            >
              {loading ? 'Changing...' : 'Change Password'}
            </Button>
          </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
